import React, { useState } from 'react';
import { LeaveBalanceSnapshot } from '../../types/leavePolicy';
import { LeaveRequest, LeaveStatus } from '../../types/leave';
import { LeaveBalanceService } from '../../services/leaveBalanceService';
import { ExpiryNotes, groupExpiringGrants } from './leaveExpiry';

interface LeaveBalanceDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  snapshot: LeaveBalanceSnapshot | null;
  leaveName: string;
  historyRequests: LeaveRequest[];
  year: number;
}

export const LeaveBalanceDrawer: React.FC<LeaveBalanceDrawerProps> = ({
  isOpen,
  onClose,
  snapshot,
  leaveName,
  historyRequests,
  year,
}) => {
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  if (!isOpen || !snapshot) return null;

  const filteredRequests = historyRequests.filter(req => {
    if (statusFilter === 'ALL') return true;
    return req.status.toUpperCase() === statusFilter.toUpperCase();
  });

  /*
   * The four figures this drawer exists to explain.
   *
   * It used to list the ingredients (granted, carry-forward, comp-off, net HR adjustment)
   * and then jump straight to the balance, with no "used" line anywhere — so an employee
   * whose leave was all taken before the system went live saw a balance below their grant
   * and nothing on the screen accounting for the difference. The one clue was a negative
   * "HR Adjustments", which reads as entitlement withheld rather than as leave taken.
   *
   * Entitlement - Used - Pending = Available holds by construction; see
   * LeaveBalanceService.totalUsed.
   */
  const entitlement = LeaveBalanceService.totalEntitlement(snapshot);
  const totalUsed = LeaveBalanceService.totalUsed(snapshot);
  const historicalUsed = LeaveBalanceService.historicalUsedOf(snapshot);
  const hrCredit = LeaveBalanceService.hrCreditOf(snapshot);

  return (
    <div className="lb-drawer-backdrop" onClick={onClose}>
      <div className="lb-drawer" onClick={e => e.stopPropagation()}>
        <div className="lb-drawer-header">
          <div>
            <h2>{leaveName} Details</h2>
            <p className="lb-drawer-subtitle">Year {year} Allotment & Application Breakdown</p>
          </div>
          <button className="lb-drawer-close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="lb-drawer-body">
          {/* Entitlement Breakdown Grid */}
          <div className="lb-drawer-stats-grid">
            <div className="lb-drawer-stat-card">
              <span className="label">Accrued from Joining</span>
              <span className="value">{snapshot.granted}</span>
            </div>
            <div className="lb-drawer-stat-card">
              <span className="label">Carry Forward</span>
              <span className="value">+{snapshot.carryForward}</span>
            </div>
            <div className="lb-drawer-stat-card">
              <span className="label">Comp-Off Earned</span>
              <span className="value">+{snapshot.compOffEarned}</span>
            </div>
            {/* Credits only. A negative adjustment is leave already taken and is counted
                under Used below, not shown here as entitlement withheld. */}
            <div className="lb-drawer-stat-card">
              <span className="label">HR Credits</span>
              <span className="value">+{hrCredit}</span>
            </div>
            <div className="lb-drawer-stat-card highlight">
              <span className="label">Total Allotment</span>
              <span className="value">{entitlement}</span>
            </div>
            <div className="lb-drawer-stat-card">
              <span className="label">Used</span>
              <span className="value">−{totalUsed}</span>
              {/* Named explicitly, because an employee who never filed these requests would
                  otherwise have no way to tell where the days went. */}
              {historicalUsed > 0 && (
                <span className="lb-drawer-stat-note neutral">
                  includes {historicalUsed} recorded by HR before go-live
                  {snapshot.consumed > 0 && ` · ${snapshot.consumed} applied in the app`}
                </span>
              )}
            </div>
            <div className="lb-drawer-stat-card pending">
              <span className="label">Pending Requests</span>
              <span className="value">−{snapshot.pending}</span>
            </div>
            <div className="lb-drawer-stat-card highlight">
              <span className="label">Available Balance</span>
              <span className="value">{snapshot.balance}</span>
            </div>
          </div>

          {/*
            Expiry gets its own row rather than sitting inside a stat card.
            It used to be rendered under "HR Adjustments", but expiringGrants mixes two
            sources — dated HR grants *and*, for Comp-Off, the earned days themselves — and
            carries no marker saying which is which. On a Comp-Off balance that meant the
            comp-off deadlines were captioned as HR adjustments, which is simply the wrong
            attribution. A row of its own is true for both sources and has room for the date.
          */}
          {groupExpiringGrants(snapshot.expiringGrants).length > 0 && (
            <div className="lb-drawer-expiry">
              <span className="lb-drawer-expiry-label">On a clock</span>
              <div className="lb-drawer-expiry-list">
                <ExpiryNotes grants={snapshot.expiringGrants} />
              </div>
            </div>
          )}

          {/* History Header & Status Filter */}
          <div className="lb-drawer-history-bar">
            <h3>Leave History & Requests</h3>
            <select
              className="lb-status-filter-select"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              {/* Derived from the LeaveStatus enum — see EmployeeLeaveProfileDrawer for why
                  the hand-written list was wrong. */}
              <option value="ALL">All Statuses</option>
              {Object.values(LeaveStatus).map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>

          {/* History Table */}
          {filteredRequests.length === 0 ? (
            <div className="lb-empty-history">
              <p>No leave requests found for {leaveName} matching filter "{statusFilter}".</p>
            </div>
          ) : (
            <table className="lb-history-table">
              <thead>
                <tr>
                  <th>Dates</th>
                  <th>Leave Type</th>
                  <th>Reason</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map(req => (
                  <tr key={req.id}>
                    <td>
                      {req.startDate} to {req.endDate}
                    </td>
                    <td>{req.type}</td>
                    <td className="lb-reason-cell">{req.reason || 'N/A'}</td>
                    <td>
                      <span className={`lb-status-badge status-${req.status.toLowerCase()}`}>
                        {req.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
